/**
 * PropertyAhrefs — the Ahrefs data-layer view for one property.
 *
 * 10-tab layout (Sprint D):
 *
 *   Summary       — headline KPIs (DR, refdomains, keywords, traffic)
 *   Backlinks     — unified inbound view with filter pills
 *                   (Live · New 30d · Lost 30d · Broken · Dofollow)
 *   Refdomains    — referring domains + referring IPs sub-tab
 *   Anchors       — inbound + internal anchors sub-tab
 *   Outbound      — linked-domains + linked-anchors-external + outlinks-stats
 *   Pages         — top by traffic / by backlinks / by internal links
 *   Keywords      — organic ranking + Keywords Explorer research surface
 *   Competitors   — organic competitors
 *   AI visibility — Brand Radar (SOV, mentions, cited domains, AI responses)
 *   Geo           — per-country traffic split
 *
 * Reads exclusively from the most-recent source_data row per endpoint
 * (via useCapabilitySample). Refresh button triggers a bulk pull of
 * every wired Ahrefs endpoint for this property.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  RefreshCw, Loader2, ExternalLink, AlertTriangle, BarChart3,
  Link as LinkIcon, Tag, Globe, Users,
  Sparkles, Network, Map as MapIcon, LinkIcon as LinkOut,
  ArrowUpRight,
} from "lucide-react";
import {
  useProperty, useCapabilitySample,
  useStartAhrefsPull, useKeywordResearch,
  type KeywordResearchTerm,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


type Tab =
  | "summary"
  | "backlinks"
  | "refdomains"
  | "anchors"
  | "outbound"
  | "pages"
  | "keywords"
  | "competitors"
  | "ai_visibility"
  | "geo";

type BacklinksFilter = "live" | "new" | "broken" | "dofollow";
// 'lost' filter removed pending Ahrefs API support — Auriel-side
// lost detection (via substrate staleness) will surface on the
// Backlinks lens (cross-source) instead.
type AnchorsSubtab    = "inbound" | "internal";
type PagesSubtab      = "by_traffic" | "by_backlinks" | "by_internal_links";


export default function PropertyAhrefs() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const [tab, setTab] = useState<Tab>("summary");
  const pull = useStartAhrefsPull(propertyId);

  const onRefresh = async () => {
    if (!confirm(
      "Pull every wired Ahrefs endpoint for this property? "
      + "~30 endpoints. Uses ~150-500 Ahrefs units. Cached results "
      + "served if fresh (within 24h)."
    )) return;
    try { await pull.mutateAsync({ force: false }); } catch { /* surfaces inline */ }
  };

  const onRefreshForce = async () => {
    if (!confirm(
      "Force-refresh ALL Ahrefs endpoints (bypass cache)? "
      + "Uses ~500-1000 Ahrefs units. Takes 1-2 minutes."
    )) return;
    try { await pull.mutateAsync({ force: true }); } catch { /* surfaces inline */ }
  };

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">Ahrefs</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Site Explorer + Brand Radar view of <span className="font-mono">{p.domain}</span>.
            Data from the most recent cached pull. Click <strong>Refresh</strong> to pull fresh.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={pull.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {pull.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
          <button
            type="button"
            onClick={onRefreshForce}
            disabled={pull.isPending}
            title="Force fresh pull, bypass cache"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-xs hover:bg-accent disabled:opacity-50"
          >
            Force
          </button>
        </div>
      </div>

      {pull.error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
          {pull.error instanceof Error ? pull.error.message : "Pull failed"}
        </div>
      )}

      {pull.isSuccess && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400">
          Pulled. Reload the page or click a tab to see the new data.
        </div>
      )}

      {/* Headline metrics row */}
      <SummaryRow propertyId={propertyId} />

      {/* Tab strip */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        <TabBtn active={tab === "summary"}      onClick={() => setTab("summary")}      icon={<BarChart3 className="h-3.5 w-3.5" />}>Summary</TabBtn>
        <TabBtn active={tab === "backlinks"}    onClick={() => setTab("backlinks")}    icon={<LinkIcon className="h-3.5 w-3.5" />}>Backlinks</TabBtn>
        <TabBtn active={tab === "refdomains"}   onClick={() => setTab("refdomains")}   icon={<Globe className="h-3.5 w-3.5" />}>Refdomains</TabBtn>
        <TabBtn active={tab === "anchors"}      onClick={() => setTab("anchors")}      icon={<Tag className="h-3.5 w-3.5" />}>Anchors</TabBtn>
        <TabBtn active={tab === "outbound"}     onClick={() => setTab("outbound")}     icon={<LinkOut className="h-3.5 w-3.5" />}>Outbound</TabBtn>
        <TabBtn active={tab === "pages"}        onClick={() => setTab("pages")}        icon={<ArrowUpRight className="h-3.5 w-3.5" />}>Pages</TabBtn>
        <TabBtn active={tab === "keywords"}     onClick={() => setTab("keywords")}     icon={<Tag className="h-3.5 w-3.5" />}>Keywords</TabBtn>
        <TabBtn active={tab === "competitors"}  onClick={() => setTab("competitors")}  icon={<Users className="h-3.5 w-3.5" />}>Competitors</TabBtn>
        <TabBtn active={tab === "ai_visibility"} onClick={() => setTab("ai_visibility")} icon={<Sparkles className="h-3.5 w-3.5" />}>AI visibility</TabBtn>
        <TabBtn active={tab === "geo"}          onClick={() => setTab("geo")}          icon={<MapIcon className="h-3.5 w-3.5" />}>Geo</TabBtn>
      </div>

      <div>
        {tab === "summary"        && <SummaryTab    domain={p.domain} />}
        {tab === "backlinks"      && <BacklinksTab  propertyId={propertyId} domain={p.domain} />}
        {tab === "refdomains"     && <RefDomainsTab propertyId={propertyId} domain={p.domain} />}
        {tab === "anchors"        && <AnchorsTab    propertyId={propertyId} domain={p.domain} />}
        {tab === "outbound"       && <OutboundTab   propertyId={propertyId} domain={p.domain} />}
        {tab === "pages"          && <PagesTab      propertyId={propertyId} domain={p.domain} />}
        {tab === "keywords"       && <KeywordsTab   propertyId={propertyId} domain={p.domain} />}
        {tab === "competitors"    && <CompetitorsTab propertyId={propertyId} domain={p.domain} />}
        {tab === "ai_visibility"  && <AIVisibilityTab propertyId={propertyId} domain={p.domain} reportId={p.brand_radar_report_id} />}
        {tab === "geo"            && <GeoTab        propertyId={propertyId} domain={p.domain} />}
      </div>
    </div>
  );
}


function TabBtn({ active, onClick, children, icon }: {
  active: boolean; onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {icon}
      {children}
    </button>
  );
}


// ─── Summary cards (DR, ref domains, organic keywords, traffic) ────


function SummaryRow({ propertyId }: { propertyId: number }) {
  const dr      = useCapabilitySample("ahrefs", "site-explorer/domain-rating",   { propertyId });
  const blStats = useCapabilitySample("ahrefs", "site-explorer/backlinks-stats", { propertyId });
  const metrics = useCapabilitySample("ahrefs", "site-explorer/metrics",         { propertyId });

  const drVal = (dr.data?.response as Record<string, unknown> | undefined);
  const drNum = ((drVal?.domain_rating as Record<string, unknown> | undefined)?.domain_rating as number | undefined);
  const ahrefsRank = ((drVal?.domain_rating as Record<string, unknown> | undefined)?.ahrefs_rank as number | undefined);

  const blMetrics = ((blStats.data?.response as Record<string, unknown> | undefined)?.metrics as Record<string, number> | undefined);
  const refDomains = blMetrics?.live_refdomains;
  const liveBacklinks = blMetrics?.live;

  const mMetrics = ((metrics.data?.response as Record<string, unknown> | undefined)?.metrics as Record<string, number> | undefined);
  const orgKeywords = mMetrics?.org_keywords;
  const orgTraffic = mMetrics?.org_traffic;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Headline label="Domain Rating"        value={drNum ?? "—"}        suffix={ahrefsRank ? `(rank #${ahrefsRank.toLocaleString()})` : null} />
      <Headline label="Referring domains"    value={refDomains}          sub={liveBacklinks != null ? `${liveBacklinks.toLocaleString()} backlinks` : null} />
      <Headline label="Organic keywords"     value={orgKeywords}         sub="last 24h cache" />
      <Headline label="Organic traffic / mo" value={orgTraffic}          sub="estimated" />
    </div>
  );
}


function Headline({ label, value, sub, suffix }: {
  label: string;
  value: number | string | undefined;
  sub?: string | null;
  suffix?: string | null;
}) {
  const display = value === undefined || value === null
    ? "no data"
    : (typeof value === "number" ? value.toLocaleString() : value);
  const isNoData = value === undefined || value === null;
  return (
    <Card>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn(
        "mt-0.5 text-2xl font-semibold",
        isNoData && "text-muted-foreground italic",
      )}>
        {display}
      </div>
      {(sub || suffix) && (
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {suffix && <span>{suffix}</span>}
          {sub && <span>{sub}</span>}
        </div>
      )}
    </Card>
  );
}


// ─── Sub-pill component for filter strips ──────────────────────────


function SubPill({ active, onClick, children, count }: {
  active: boolean; onClick: () => void;
  children: React.ReactNode;
  count?: number | string;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
      )}>
      {children}
      {count !== undefined && (
        <span className={cn(
          "rounded-sm px-1 text-[10px]",
          active ? "bg-primary-foreground/20" : "bg-background/50",
        )}>
          {count}
        </span>
      )}
    </button>
  );
}


// ─── Tab content components ────────────────────────────────────────


function SummaryTab({ domain }: { domain: string }) {
  return (
    <Card>
      <div className="text-sm">
        Cross-tab overview for <span className="font-mono">{domain}</span>.
        Each tab below reads from the latest pulled snapshot of the
        relevant Ahrefs endpoint set.
      </div>
      <div className="mt-3 text-xs text-muted-foreground">
        Tabs:
        <ul className="ml-4 mt-1 list-disc space-y-0.5">
          <li><strong>Backlinks</strong> — inbound links with new/lost/broken filters.</li>
          <li><strong>Refdomains</strong> — per-domain inbound view + referring IPs.</li>
          <li><strong>Anchors</strong> — inbound anchor distribution + internal anchors.</li>
          <li><strong>Outbound</strong> — what we link out to (E-E-A-T signal).</li>
          <li><strong>Pages</strong> — top pages by traffic, backlinks, internal links.</li>
          <li><strong>Keywords</strong> — organic ranking keywords.</li>
          <li><strong>Competitors</strong> — domains sharing our keyword universe.</li>
          <li><strong>AI visibility</strong> — Brand Radar share-of-voice in AI search.</li>
          <li><strong>Geo</strong> — per-country traffic split.</li>
        </ul>
      </div>
    </Card>
  );
}


function BacklinksTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const [filter, setFilter] = useState<BacklinksFilter>("live");

  const allBl   = useCapabilitySample("ahrefs", "site-explorer/all-backlinks",    { propertyId });
  const newBl   = useCapabilitySample("ahrefs", "site-explorer/new-backlinks",    { propertyId });
  const broken  = useCapabilitySample("ahrefs", "site-explorer/broken-backlinks", { propertyId });

  const liveOnly = ((allBl.data?.response as Record<string, unknown> | undefined)
    ?.backlinks as Array<Record<string, unknown>> | undefined) ?? [];
  const dofollowOnly = liveOnly.filter((r) => Boolean(r.is_dofollow));

  const sample =
      filter === "broken" ? broken
    : filter === "new"    ? newBl
    : allBl;

  const rows: Array<Record<string, unknown>> =
      filter === "live"     ? liveOnly
    : filter === "new"      ? (((newBl.data?.response as Record<string, unknown> | undefined)?.backlinks as Array<Record<string, unknown>> | undefined) ?? [])
    : filter === "broken"   ? (((broken.data?.response as Record<string, unknown> | undefined)?.backlinks as Array<Record<string, unknown>> | undefined) ?? []).filter((r) => {
        const code = (r as { http_code?: number }).http_code;
        return typeof code === "number" && code >= 400;
      })
    : filter === "dofollow" ? dofollowOnly
    : [];

  const liveRows     = liveOnly.length;
  const newRows      = ((newBl.data?.response  as Record<string, unknown> | undefined)?.backlinks as unknown[] | undefined)?.length ?? 0;
  const brokenRows   = ((broken.data?.response as Record<string, unknown> | undefined)?.backlinks as unknown[] | undefined)?.filter((r) => {
    const code = (r as { http_code?: number }).http_code;
    return typeof code === "number" && code >= 400;
  })?.length ?? 0;
  const dofollowRows = dofollowOnly.length;

  const subtitleByFilter: Record<BacklinksFilter, string> = {
    live:     "Live backlinks — top by source DR.",
    new:      "New backlinks — most recently first-seen by Ahrefs (top 200).",
    broken:   "Broken backlinks — links pointing at 4xx URLs on our site. Recoverable with 301 redirects.",
    dofollow: "Live dofollow backlinks only — the link equity that actually counts.",
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <SubPill active={filter === "live"}     onClick={() => setFilter("live")}     count={liveRows}>Live</SubPill>
        <SubPill active={filter === "new"}      onClick={() => setFilter("new")}      count={newRows}>New 30d</SubPill>
        <SubPill active={filter === "broken"}   onClick={() => setFilter("broken")}   count={brokenRows}>Broken</SubPill>
        <SubPill active={filter === "dofollow"} onClick={() => setFilter("dofollow")} count={dofollowRows}>Dofollow</SubPill>
      </div>
      <div className="text-[11px] text-muted-foreground">
        Lost-backlink tracking is on the <strong>Backlinks lens</strong> (cross-source) —
        Auriel-side substrate marks links lost when they disappear from successive Ahrefs pulls.
      </div>

      <DataTable
        title={`Backlinks for ${domain}`}
        subtitle={subtitleByFilter[filter]}
        sample={sample.data}
        rowCount={rows.length}
        columns={[
          { key: "url_from",   label: "Source page", className: "font-mono text-[11px] max-w-[28%]",
            render: (r) => (
              <a href={String(r.url_from ?? "")} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-0.5 hover:underline" title={String(r.url_from ?? "")}>
                <span className="truncate">{stripScheme(String(r.url_from ?? ""))}</span>
                <ExternalLink className="h-3 w-3 opacity-50" />
              </a>
            )},
          { key: "url_to",     label: "Target URL", className: "font-mono text-[11px] max-w-[24%]",
            render: (r) => (
              <a href={String(r.url_to ?? "")} target="_blank" rel="noreferrer"
                 className="hover:underline" title={String(r.url_to ?? "")}>
                {stripScheme(String(r.url_to ?? ""))}
              </a>
            )},
          { key: "anchor", label: "Anchor", className: "max-w-[16%] truncate" },
          { key: "domain_rating_source", label: "DR", align: "right",
            render: (r) => Number(r.domain_rating_source ?? 0).toFixed(0) },
          { key: "is_dofollow", label: "Type", align: "right",
            render: (r) => r.is_dofollow ? "dofollow" : <span className="text-muted-foreground">nofollow</span> },
          ...(filter === "broken" ? [{
            key: "http_code", label: "Code", align: "right" as const,
          }] : []),
          { key: "first_seen", label: "First seen", align: "right",
            render: (r) => formatDateShort(String(r.first_seen ?? "")) },
        ]}
        rows={rows}
        icon={<LinkIcon className="h-5 w-5 text-primary" />}
      />
    </div>
  );
}


function RefDomainsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const domainsSample = useCapabilitySample("ahrefs", "site-explorer/refdomains", { propertyId });

  return (
    <DataTable
      title={`Referring domains for ${domain}`}
      subtitle="Top referring domains by Ahrefs DR (top 100 per pull). Substrate-backed view with lifecycle is on the Backlinks lens."
      sample={domainsSample.data}
      rowCount={(((domainsSample.data?.response as Record<string, unknown> | undefined)?.refdomains as unknown[] | undefined)?.length) ?? 0}
      columns={[
        { key: "domain", label: "Referring domain", className: "font-mono text-[11px]" },
        { key: "domain_rating", label: "DR", align: "right" },
        { key: "links_to_target", label: "Links", align: "right",
          render: (r) => Number(r.links_to_target ?? 0).toLocaleString() },
        { key: "dofollow_linked_domains", label: "Dofollow", align: "right" },
        { key: "first_seen", label: "First seen", align: "right",
          render: (r) => formatDateShort(String(r.first_seen ?? "")) },
      ]}
      rows={(((domainsSample.data?.response as Record<string, unknown> | undefined)?.refdomains as Array<Record<string, unknown>> | undefined)) ?? []}
      icon={<LinkIcon className="h-5 w-5 text-primary" />}
    />
  );
}


function AnchorsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const [sub, setSub] = useState<AnchorsSubtab>("inbound");
  const inbound  = useCapabilitySample("ahrefs", "site-explorer/anchors", { propertyId });
  const internal = useCapabilitySample("ahrefs", "site-explorer/linked-anchors-internal", { propertyId });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <SubPill active={sub === "inbound"}  onClick={() => setSub("inbound")}>Inbound (anchors used by others)</SubPill>
        <SubPill active={sub === "internal"} onClick={() => setSub("internal")}>Internal (anchors we use ourselves)</SubPill>
      </div>

      {sub === "inbound" ? (
        <DataTable
          title={`Inbound anchor distribution for ${domain}`}
          subtitle="Top anchor texts by link count. Spam-flagged anchors highlighted."
          sample={inbound.data}
          rowCount={(((inbound.data?.response as Record<string, unknown> | undefined)?.anchors as unknown[] | undefined)?.length) ?? 0}
          columns={[
            { key: "anchor", label: "Anchor text", className: "max-w-[40%] truncate",
              render: (r) => {
                const isSpam = Boolean(r.is_spam);
                return (
                  <span className={cn(isSpam && "text-red-600 dark:text-red-400")}>
                    {isSpam && <AlertTriangle className="mr-1 inline h-3 w-3" />}
                    {String(r.anchor ?? "(empty)")}
                  </span>
                );
              }},
            { key: "refdomains",       label: "Ref domains", align: "right" },
            { key: "links_to_target",  label: "Links",       align: "right",
              render: (r) => Number(r.links_to_target ?? 0).toLocaleString() },
            { key: "top_domain_rating", label: "Top DR", align: "right" },
            { key: "dofollow_links",   label: "Dofollow", align: "right" },
          ]}
          rows={(((inbound.data?.response as Record<string, unknown> | undefined)?.anchors as Array<Record<string, unknown>> | undefined)) ?? []}
          icon={<Tag className="h-5 w-5 text-primary" />}
        />
      ) : (
        <DataTable
          title={`Internal anchor distribution for ${domain}`}
          subtitle="Anchors we use in our own internal links. Brand-anchor overuse + 'click here' dilution surface here."
          sample={internal.data}
          rowCount={(((internal.data?.response as Record<string, unknown> | undefined)?.anchors as unknown[] | undefined)?.length) ?? 0}
          columns={[
            { key: "anchor",            label: "Anchor text", className: "max-w-[40%] truncate" },
            { key: "linked_pages",      label: "Pages linked", align: "right",
              render: (r) => Number(r.linked_pages ?? 0).toLocaleString() },
            { key: "links_from_target", label: "Total links", align: "right",
              render: (r) => Number(r.links_from_target ?? 0).toLocaleString() },
            { key: "dofollow_links",    label: "Dofollow", align: "right" },
            { key: "first_seen", label: "First seen", align: "right",
              render: (r) => formatDateShort(String(r.first_seen ?? "")) },
          ]}
          rows={(((internal.data?.response as Record<string, unknown> | undefined)?.anchors as Array<Record<string, unknown>> | undefined)) ?? []}
          icon={<Tag className="h-5 w-5 text-primary" />}
        />
      )}
    </div>
  );
}


function OutboundTab({ propertyId, domain: _domain }: { propertyId: number; domain: string }) {
  const externalAnchors = useCapabilitySample("ahrefs", "site-explorer/linked-anchors-external", { propertyId });
  const stats = useCapabilitySample("ahrefs", "site-explorer/outlinks-stats", { propertyId });

  const statsBody = (stats.data?.response as Record<string, unknown> | undefined);

  return (
    <div className="space-y-4">
      {statsBody && (
        <Card>
          <div className="flex items-center gap-2">
            <LinkOut className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Outbound link summary</h3>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatRow label="Total outlinks"   value={Number(statsBody.linked_pages ?? statsBody.total ?? 0)} />
            <StatRow label="Linked domains"   value={Number(statsBody.linked_domains ?? 0)} />
            <StatRow label="Dofollow outlinks" value={Number(statsBody.dofollow ?? 0)} />
            <StatRow label="Nofollow outlinks" value={Number(statsBody.nofollow ?? 0)} />
          </div>
        </Card>
      )}

      <DataTable
        title="Anchors we use when linking out"
        subtitle="Anchor texts of OUR outbound links. E-E-A-T signal — descriptive anchors > naked URLs."
        sample={externalAnchors.data}
        rowCount={(((externalAnchors.data?.response as Record<string, unknown> | undefined)?.anchors as unknown[] | undefined)?.length) ?? 0}
        columns={[
          { key: "anchor",            label: "Anchor text", className: "max-w-[40%] truncate" },
          { key: "linked_domains",    label: "Linked domains", align: "right",
            render: (r) => Number(r.linked_domains ?? 0).toLocaleString() },
          { key: "linked_pages",      label: "Linked pages", align: "right",
            render: (r) => Number(r.linked_pages ?? 0).toLocaleString() },
          { key: "links_from_target", label: "Total links", align: "right",
            render: (r) => Number(r.links_from_target ?? 0).toLocaleString() },
          { key: "dofollow_links",    label: "Dofollow", align: "right" },
        ]}
        rows={(((externalAnchors.data?.response as Record<string, unknown> | undefined)?.anchors as Array<Record<string, unknown>> | undefined)) ?? []}
        icon={<Tag className="h-5 w-5 text-primary" />}
      />
    </div>
  );
}


function PagesTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const [sub, setSub] = useState<PagesSubtab>("by_traffic");
  const traffic       = useCapabilitySample("ahrefs", "site-explorer/top-pages", { propertyId });
  const backlinks     = useCapabilitySample("ahrefs", "site-explorer/pages-by-backlinks", { propertyId });
  const internalLinks = useCapabilitySample("ahrefs", "site-explorer/pages-by-internal-links", { propertyId });

  const sample = sub === "by_traffic" ? traffic
               : sub === "by_backlinks" ? backlinks
               : internalLinks;
  const rows = ((sample.data?.response as Record<string, unknown> | undefined)
    ?.pages as Array<Record<string, unknown>> | undefined) ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <SubPill active={sub === "by_traffic"}   onClick={() => setSub("by_traffic")}>By organic traffic</SubPill>
        <SubPill active={sub === "by_backlinks"} onClick={() => setSub("by_backlinks")}>By inbound backlinks</SubPill>
        <SubPill active={sub === "by_internal_links"} onClick={() => setSub("by_internal_links")}>By internal links</SubPill>
      </div>

      <DataTable
        title={`Pages of ${domain}`}
        subtitle={
          sub === "by_traffic" ? "Pages ranked by Ahrefs-estimated organic traffic."
          : sub === "by_backlinks" ? "Pages with the most inbound backlinks. Focus internal-link audits on these."
          : "Pages with the most INBOUND internal links. The site's structural pillar pages."
        }
        sample={sample.data}
        rowCount={rows.length}
        columns={
          sub === "by_traffic" ? [
            { key: "url", label: "URL", className: "font-mono text-[11px] max-w-[36%]",
              render: (r) => (
                <a href={String(r.url ?? "")} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-0.5 hover:underline" title={String(r.url ?? "")}>
                  <span className="truncate">{stripScheme(String(r.url ?? ""))}</span>
                  <ExternalLink className="h-3 w-3 opacity-50" />
                </a>
              )},
            { key: "sum_traffic", label: "Est. traffic", align: "right",
              render: (r) => Number(r.sum_traffic ?? 0).toLocaleString() },
            { key: "keywords", label: "Keywords", align: "right" },
            { key: "value", label: "Traffic value", align: "right",
              render: (r) => `$${Number(r.value ?? 0).toLocaleString()}` },
            { key: "top_keyword", label: "Top keyword", className: "max-w-[20%] truncate" },
            { key: "top_keyword_best_position", label: "Pos", align: "right" },
          ] : sub === "by_backlinks" ? [
            { key: "url_to", label: "URL", className: "font-mono text-[11px] max-w-[36%]",
              render: (r) => (
                <a href={String(r.url_to ?? "")} target="_blank" rel="noreferrer"
                   className="hover:underline" title={String(r.url_to ?? "")}>
                  {stripScheme(String(r.url_to ?? ""))}
                </a>
              )},
            { key: "refdomains_target", label: "Ref domains", align: "right",
              render: (r) => Number(r.refdomains_target ?? 0).toLocaleString() },
            { key: "dofollow_to_target", label: "Dofollow", align: "right",
              render: (r) => Number(r.dofollow_to_target ?? 0).toLocaleString() },
            { key: "url_rating_target", label: "UR", align: "right" },
            { key: "is_spam", label: "Spam?", align: "right",
              render: (r) => r.is_spam ? <span className="text-red-600 dark:text-red-400">yes</span> : "—" },
            { key: "first_seen_link", label: "First seen", align: "right",
              render: (r) => formatDateShort(String(r.first_seen_link ?? "")) },
          ] : [
            { key: "url_to", label: "URL", className: "font-mono text-[11px] max-w-[40%]",
              render: (r) => (
                <a href={String(r.url_to ?? "")} target="_blank" rel="noreferrer"
                   className="hover:underline" title={String(r.url_to ?? "")}>
                  {stripScheme(String(r.url_to ?? ""))}
                </a>
              )},
            { key: "links_to_target", label: "Internal links", align: "right",
              render: (r) => Number(r.links_to_target ?? 0).toLocaleString() },
            { key: "dofollow_to_target", label: "Dofollow", align: "right",
              render: (r) => Number(r.dofollow_to_target ?? 0).toLocaleString() },
            { key: "nofollow_to_target", label: "Nofollow", align: "right" },
            { key: "url_rating_target", label: "UR", align: "right" },
            { key: "http_code_target",  label: "HTTP", align: "right" },
          ]
        }
        rows={rows}
        icon={<Globe className="h-5 w-5 text-primary" />}
      />
    </div>
  );
}


function KeywordsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const [view, setView] = useState<"ranking" | "research">("ranking");
  const sample = useCapabilitySample("ahrefs", "site-explorer/organic-keywords", { propertyId });
  const rows = ((sample.data?.response as Record<string, unknown> | undefined)
    ?.keywords as Array<Record<string, unknown>> | undefined) ?? [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <SubPill active={view === "ranking"}  onClick={() => setView("ranking")}>What we rank for</SubPill>
        <SubPill active={view === "research"} onClick={() => setView("research")}>Research keywords</SubPill>
      </div>

      {view === "ranking" ? (
        <DataTable
          title={`Organic keywords for ${domain}`}
          subtitle="Keywords this site ranks for in the configured country. Currently pulling top 200."
          sample={sample.data}
          rowCount={rows.length}
          columns={[
            { key: "keyword",       label: "Keyword",      className: "max-w-[26%] truncate" },
            { key: "best_position", label: "Position",     align: "right" },
            { key: "volume",        label: "Volume",       align: "right",
              render: (r) => Number(r.volume ?? 0).toLocaleString() },
            { key: "sum_traffic",   label: "Est. traffic", align: "right",
              render: (r) => Number(r.sum_traffic ?? 0).toLocaleString() },
            { key: "keyword_difficulty", label: "KD",      align: "right" },
            { key: "best_position_url",  label: "Landing URL", className: "font-mono text-[11px] max-w-[26%]",
              render: (r) => (
                <a href={String(r.best_position_url ?? "")} target="_blank" rel="noreferrer"
                   className="hover:underline" title={String(r.best_position_url ?? "")}>
                  {stripScheme(String(r.best_position_url ?? ""))}
                </a>
              )},
          ]}
          rows={rows}
          icon={<Tag className="h-5 w-5 text-primary" />}
        />
      ) : (
        <KeywordResearchPanel propertyId={propertyId} />
      )}
    </div>
  );
}


function KeywordResearchPanel({ propertyId }: { propertyId: number }) {
  const [seed, setSeed] = useState("");
  const [country, setCountry] = useState("ro");
  const research = useKeywordResearch(propertyId);

  const onSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!seed.trim()) return;
    research.mutate({ keyword: seed.trim(), country });
  };

  const data = research.data;
  const ov = data?.overview;

  return (
    <Card>
      <form onSubmit={onSearch} className="space-y-3">
        <div>
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Keyword research</h3>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Discover keywords to TARGET (not just what we rank for today). Uses Ahrefs Keywords Explorer.
            ~10-30 units per query. Cached 7 days per seed+country combo.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[11px] font-medium text-muted-foreground">Seed keyword</label>
            <input type="text" value={seed} onChange={(e) => setSeed(e.target.value)}
              placeholder="e.g. airsoft, replici airsoft..."
              className="mt-0.5 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono" />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-muted-foreground">Country</label>
            <select value={country} onChange={(e) => setCountry(e.target.value)}
              className="mt-0.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono">
              <option value="ro">RO (Romania)</option>
              <option value="us">US</option>
              <option value="gb">GB</option>
              <option value="de">DE</option>
              <option value="it">IT</option>
              <option value="fr">FR</option>
              <option value="es">ES</option>
            </select>
          </div>
          <button type="submit" disabled={research.isPending || !seed.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {research.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />}
            Search
          </button>
        </div>

        {research.error && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
            {research.error instanceof Error ? research.error.message : "Research failed"}
          </div>
        )}
      </form>

      {data && (
        <div className="mt-4 space-y-4">
          {/* Overview */}
          {ov && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Seed overview — "{data.keyword}" in {data.country.toUpperCase()}
              </h4>
              <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-5">
                <StatRow label="Volume"     value={ov.volume ?? "—"} />
                <StatRow label="Difficulty" value={ov.difficulty ?? "—"} />
                <StatRow label="CPC"        value={ov.cpc != null ? `$${ov.cpc.toFixed(2)}` : "—"} />
                <StatRow label="Clicks/mo"  value={ov.clicks ?? "—"} />
                <StatRow label="Commercial?" value={ov.is_commercial ? "yes" : "no"} />
              </div>
            </div>
          )}

          {/* Matching terms */}
          {data.matching_terms && data.matching_terms.length > 0 && (
            <ResearchTable
              title="Matching terms (long-tail expansion)"
              subtitle={`Phrases containing "${data.keyword}". Top by volume.`}
              rows={data.matching_terms}
            />
          )}

          {/* Related terms */}
          {data.related_terms && data.related_terms.length > 0 && (
            <ResearchTable
              title="Related terms (semantic neighbors)"
              subtitle="Related keywords Ahrefs's neighbor model surfaced."
              rows={data.related_terms}
            />
          )}

          {/* Per-endpoint errors */}
          {Object.keys(data.errors).length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs">
              <div className="font-medium text-amber-700 dark:text-amber-400">Partial errors:</div>
              <ul className="ml-4 mt-1 list-disc space-y-0.5">
                {Object.entries(data.errors).map(([k, v]) => (
                  <li key={k}><strong>{k}:</strong> {v.slice(0, 120)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


function ResearchTable({ title, subtitle, rows }: {
  title: string;
  subtitle: string;
  rows: KeywordResearchTerm[];
}) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</h4>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</p>
      <div className="mt-2 overflow-x-auto rounded-md border border-border">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border bg-muted/30 text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Keyword</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Volume</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">KD</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">CPC</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Clicks/mo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.slice(0, 50).map((r, i) => (
              <tr key={i} className="hover:bg-accent/40">
                <td className="px-2 py-1.5 max-w-[40%] truncate">{r.keyword}</td>
                <td className="px-2 py-1.5 text-right">{r.volume != null ? Number(r.volume).toLocaleString() : "—"}</td>
                <td className="px-2 py-1.5 text-right">{r.difficulty ?? "—"}</td>
                <td className="px-2 py-1.5 text-right">{r.cpc != null ? `$${Number(r.cpc).toFixed(2)}` : "—"}</td>
                <td className="px-2 py-1.5 text-right">{r.clicks != null ? Number(r.clicks).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 50 && (
          <div className="border-t border-border bg-muted/20 px-2 py-1 text-[10px] text-muted-foreground">
            Showing 50 of {rows.length.toLocaleString()} rows
          </div>
        )}
      </div>
    </div>
  );
}


function CompetitorsTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("ahrefs", "site-explorer/organic-competitors", { propertyId });
  const rows = ((sample.data?.response as Record<string, unknown> | undefined)
    ?.competitors as Array<Record<string, unknown>> | undefined) ?? [];

  return (
    <DataTable
      title={`Organic competitors of ${domain}`}
      subtitle="Domains that share the most organic keywords with us. Currently pulling top 30."
      sample={sample.data}
      rowCount={rows.length}
      columns={[
        { key: "competitor_domain", label: "Domain", className: "font-mono text-[11px]" },
        { key: "domain_rating",     label: "DR",     align: "right" },
        { key: "keywords_common",   label: "Common kw", align: "right",
          render: (r) => Number(r.keywords_common ?? 0).toLocaleString() },
        { key: "keywords_competitor", label: "Their total kw", align: "right",
          render: (r) => Number(r.keywords_competitor ?? 0).toLocaleString() },
        { key: "traffic", label: "Their est. traffic", align: "right",
          render: (r) => Number(r.traffic ?? 0).toLocaleString() },
      ]}
      rows={rows}
      icon={<Users className="h-5 w-5 text-primary" />}
    />
  );
}


function AIVisibilityTab({ propertyId, domain, reportId }: {
  propertyId: number; domain: string; reportId: string | null;
}) {
  const sov         = useCapabilitySample("ahrefs", "brand-radar/sov-overview", { propertyId });
  const sovHistory  = useCapabilitySample("ahrefs", "brand-radar/sov-history", { propertyId });
  const mentions    = useCapabilitySample("ahrefs", "brand-radar/mentions-overview", { propertyId });
  const cited       = useCapabilitySample("ahrefs", "brand-radar/cited-domains", { propertyId });
  const aiResponses = useCapabilitySample("ahrefs", "brand-radar/ai-responses", { propertyId });

  if (!reportId) {
    return (
      <Card>
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold">Brand Radar not configured</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                AI search visibility tracking (share-of-voice in Google AI
                Overview, Perplexity, ChatGPT search, etc) requires a Brand
                Radar Report from the Ahrefs dashboard.
              </p>
            </div>
            <ol className="ml-4 list-decimal space-y-1 text-xs text-muted-foreground">
              <li>Open Ahrefs → Brand Radar → Create new Report for <span className="font-mono">{domain}</span></li>
              <li>Configure the brand terms + competitors to track</li>
              <li>Copy the Report ID from the URL</li>
              <li>Paste it into <strong>Property settings → Brand Radar Report ID</strong></li>
              <li>Click <strong>Refresh</strong> at the top of this page to pull initial data</li>
            </ol>
          </div>
        </div>
      </Card>
    );
  }

  const sovBody       = (sov.data?.response as Record<string, unknown> | undefined);
  const mentionsBody  = (mentions.data?.response as Record<string, unknown> | undefined);
  const sovValue      = (sovBody?.share_of_voice ?? sovBody?.sov ?? null) as number | null;
  const totalMentions = (mentionsBody?.total_mentions ?? mentionsBody?.mentions ?? null) as number | null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="AI Share-of-voice" value={sovValue != null ? `${sovValue.toFixed(1)}%` : "—"} sub="last 30d" />
        <Headline label="Total mentions" value={totalMentions ?? "—"} sub="last 30d" />
        <Headline label="Cited domains"
          value={(((cited.data?.response as Record<string, unknown> | undefined)?.domains as unknown[] | undefined)?.length) ?? "—"}
          sub="alongside us" />
        <Headline label="AI responses"
          value={(((aiResponses.data?.response as Record<string, unknown> | undefined)?.responses as unknown[] | undefined)?.length) ?? "—"}
          sub="last 30d" />
      </div>

      <DataTable
        title="Cited domains (AI competitive landscape)"
        subtitle="Other domains AI models cite alongside us in responses about our tracked topics."
        sample={cited.data}
        rowCount={(((cited.data?.response as Record<string, unknown> | undefined)?.domains as unknown[] | undefined)?.length) ?? 0}
        columns={[
          { key: "domain",         label: "Domain", className: "font-mono text-[11px]" },
          { key: "mentions",       label: "Mentions", align: "right" },
          { key: "share_of_voice", label: "SOV", align: "right",
            render: (r) => r.share_of_voice != null ? `${Number(r.share_of_voice).toFixed(1)}%` : "—" },
        ]}
        rows={(((cited.data?.response as Record<string, unknown> | undefined)?.domains as Array<Record<string, unknown>> | undefined)) ?? []}
        icon={<Network className="h-5 w-5 text-primary" />}
      />

      <DataTable
        title="AI response snippets citing us"
        subtitle="Actual AI-generated answers that mentioned our brand. Quote-able evidence for reports."
        sample={aiResponses.data}
        rowCount={(((aiResponses.data?.response as Record<string, unknown> | undefined)?.responses as unknown[] | undefined)?.length) ?? 0}
        columns={[
          { key: "prompt",   label: "Prompt", className: "max-w-[28%] truncate" },
          { key: "response", label: "AI response",  className: "max-w-[44%]",
            render: (r) => <span className="text-[11px] leading-snug">{String(r.response ?? "").slice(0, 240)}…</span> },
          { key: "platform", label: "Platform", className: "font-mono text-[11px]" },
          { key: "date",     label: "Date", align: "right",
            render: (r) => formatDateShort(String(r.date ?? "")) },
        ]}
        rows={(((aiResponses.data?.response as Record<string, unknown> | undefined)?.responses as Array<Record<string, unknown>> | undefined)) ?? []}
        icon={<Sparkles className="h-5 w-5 text-primary" />}
      />

      {sovHistory.data?.available && (
        <Card>
          <div className="text-xs text-muted-foreground">
            SOV history (raw data — chart coming v2). Latest pull cached {sovHistory.data?.fetched_at && formatDateAgo(sovHistory.data.fetched_at)}.
          </div>
        </Card>
      )}
    </div>
  );
}


function GeoTab({ propertyId, domain }: { propertyId: number; domain: string }) {
  const sample = useCapabilitySample("ahrefs", "site-explorer/metrics-by-country", { propertyId });
  const body = (sample.data?.response as Record<string, unknown> | undefined);
  const rows = (body?.metrics ?? body?.countries) as Array<Record<string, unknown>> | undefined ?? [];

  const totalTraffic = rows.reduce((acc, r) => acc + Number(r.org_traffic ?? 0), 0);

  return (
    <div className="space-y-3">
      <DataTable
        title={`Organic traffic by country — ${domain}`}
        subtitle="Per-country traffic split. Useful for spotting incidental international visibility or bot-traffic contamination."
        sample={sample.data}
        rowCount={rows.length}
        columns={[
          { key: "country", label: "Country", className: "font-mono text-[11px]",
            render: (r) => String(r.country ?? "").toUpperCase() },
          { key: "org_keywords", label: "Keywords",  align: "right",
            render: (r) => Number(r.org_keywords ?? 0).toLocaleString() },
          { key: "org_traffic",  label: "Est. traffic", align: "right",
            render: (r) => Number(r.org_traffic ?? 0).toLocaleString() },
          { key: "share", label: "Share", align: "right",
            render: (r) => {
              const traf = Number(r.org_traffic ?? 0);
              const pct = totalTraffic > 0 ? (traf / totalTraffic) * 100 : 0;
              return `${pct.toFixed(1)}%`;
            }},
          { key: "org_cost", label: "Traffic value", align: "right",
            render: (r) => r.org_cost != null ? `$${Number(r.org_cost).toLocaleString()}` : "—" },
        ]}
        rows={rows}
        icon={<MapIcon className="h-5 w-5 text-primary" />}
      />
    </div>
  );
}


// ─── Stat row for compact inline metrics ───────────────────────────


function StatRow({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-base font-semibold">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}


// ─── Generic data-table component ───────────────────────────────────


type Column = {
  key: string;
  label: string;
  align?: "right";
  className?: string;
  render?: (row: Record<string, unknown>) => React.ReactNode;
};


function DataTable({
  title, subtitle, columns, rows, sample, rowCount, icon,
}: {
  title: string;
  subtitle: string;
  columns: Column[];
  rows: Array<Record<string, unknown>>;
  sample: { fetched_at: string | null; available: boolean } | undefined;
  rowCount: number;
  icon?: React.ReactNode;
}) {
  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {icon}
            <h2 className="text-sm font-semibold">{title}</h2>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {sample?.available && sample.fetched_at && (
          <div className="shrink-0 text-right text-[11px] text-muted-foreground">
            <div>Cached {formatDateAgo(sample.fetched_at)}</div>
            <div>{rowCount.toLocaleString()} rows</div>
          </div>
        )}
      </div>

      {!sample?.available ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          No data yet for this endpoint. Click <strong>Refresh</strong> at the top to pull.
        </div>
      ) : rowCount === 0 ? (
        <div className="rounded-md border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          The endpoint returned 0 rows.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="border-b border-border text-left">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={cn(
                    "px-2 py-1.5 font-medium text-muted-foreground",
                    c.align === "right" && "text-right",
                  )}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-accent/40">
                  {columns.map((c) => (
                    <td key={c.key} className={cn(
                      "px-2 py-1.5 align-top",
                      c.align === "right" && "text-right",
                      c.className,
                    )}>
                      {c.render ? c.render(r) : (r[c.key] != null ? String(r[c.key]) : "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}


// ─── helpers ────────────────────────────────────────────────────────


function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}

function formatDateShort(iso: string): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

function formatDateAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const sec = Math.round((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}
